import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  Calendar,
  ChevronRight,
  ExternalLink,
  MapPin,
  Building2,
} from 'lucide-react'
import { Section } from '@/components/Section'
import { EventCard } from '@/components/EventCard'
import { LocalizedLink } from '@/components/LocalizedLink'
import { JsonLd } from '@/components/SEO/JsonLd'
import { usePageSEO } from '@/hooks/usePageSEO'
import type { SupportedLanguage } from '@/i18n'
import { localizedRoutes } from '@/i18n/routes'
import {
  getEventBySlug,
  getRelatedEvents,
  eventKey,
  isUpcoming,
  type FenixEvent,
} from '@/data/events'
import { cn } from '@/lib/utils'

/* ============================================================
   Helpers
   ============================================================ */

function formatLongDate(iso: string, lang: SupportedLanguage): string {
  const parsed = new Date(iso)
  if (isNaN(parsed.getTime())) return iso
  return new Intl.DateTimeFormat(lang === 'it' ? 'it-IT' : 'en-GB', {
    dateStyle: 'long',
  }).format(parsed)
}

/**
 * Render a human-friendly date range like "7–10 March 2023". Falls back to a
 * single long date when no end date is provided. Uses the same month/year
 * at both ends to collapse the display, or two full dates otherwise.
 */
function formatDateRange(
  start: string,
  end: string | undefined,
  lang: SupportedLanguage
): string {
  if (!end || end === start) return formatLongDate(start, lang)
  const startDate = new Date(start)
  const endDate = new Date(end)
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    return formatLongDate(start, lang)
  }
  const locale = lang === 'it' ? 'it-IT' : 'en-GB'
  const sameYear = startDate.getFullYear() === endDate.getFullYear()
  const sameMonth = sameYear && startDate.getMonth() === endDate.getMonth()

  if (sameMonth) {
    const day = new Intl.DateTimeFormat(locale, { day: 'numeric' })
    const monthYear = new Intl.DateTimeFormat(locale, {
      month: 'long',
      year: 'numeric',
    })
    return `${day.format(startDate)}–${day.format(endDate)} ${monthYear.format(endDate)}`
  }

  if (sameYear) {
    const dayMonth = new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'long',
    })
    const full = new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
    return `${dayMonth.format(startDate)} – ${full.format(endDate)}`
  }

  const full = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
  return `${full.format(startDate)} – ${full.format(endDate)}`
}

/* ============================================================
   Breadcrumb — industrial, block-based, same family as news/machines
   ============================================================ */
function Breadcrumb({ eventTitle }: { eventTitle: string }) {
  const { t } = useTranslation()
  return (
    <nav
      aria-label="Breadcrumb"
      className="border-b border-ink-200 bg-white"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4">
        <ol
          role="list"
          className="flex flex-wrap items-center gap-1.5 text-xs font-semibold tracking-[0.02em] text-ink-500"
        >
          <li>
            <LocalizedLink
              to="home"
              className="uppercase tracking-[0.14em] text-ink-600 transition-colors hover:text-brand-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 rounded-sm"
            >
              {t('eventDetail.breadcrumbHome')}
            </LocalizedLink>
          </li>
          <li aria-hidden="true" className="text-ink-300">
            <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.5} />
          </li>
          <li>
            <LocalizedLink
              to="events"
              className="uppercase tracking-[0.14em] text-ink-600 transition-colors hover:text-brand-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 rounded-sm"
            >
              {t('eventDetail.breadcrumbEvents')}
            </LocalizedLink>
          </li>
          <li aria-hidden="true" className="text-ink-300">
            <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.5} />
          </li>
          <li
            aria-current="page"
            className="text-ink-900 font-bold tracking-normal normal-case line-clamp-1 max-w-[28ch] md:max-w-none"
          >
            {eventTitle}
          </li>
        </ol>
      </div>
    </nav>
  )
}

/* ============================================================
   Event hero — full-width image, dark+blue overlay, date/location meta,
   status pill, and primary external-link CTA.
   ============================================================ */
function EventHero({
  event,
  title,
  location,
  alt,
  currentLang,
}: {
  event: FenixEvent
  title: string
  location: string
  alt: string
  currentLang: SupportedLanguage
}) {
  const { t } = useTranslation()
  const upcoming = isUpcoming(event)
  const dateFormatted = formatDateRange(event.date, event.dateEnd, currentLang)

  return (
    <section
      aria-labelledby="event-hero-title"
      className="relative isolate overflow-hidden bg-ink-900 text-white"
    >
      {/* Background image */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-20 bg-cover bg-center"
        style={{ backgroundImage: `url(${event.image})` }}
      />
      {/* Primary gradient overlay */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 bg-gradient-to-b from-ink-900/82 via-ink-900/80 to-ink-900/95"
      />
      {/* Brand multiply tint */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 mix-blend-multiply bg-brand-900/35"
      />
      {/* Diagonal stripe from right */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 right-0 w-2/5 opacity-[0.1] fx-diagonal-accent"
      />
      {/* Radial brand glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-40 top-2/3 h-96 w-96 -translate-y-1/2 rounded-full bg-brand-500/20 blur-3xl"
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-20 md:pt-28 pb-16 md:pb-20">
        {/* Left engineering tick rail */}
        <div
          aria-hidden="true"
          className="absolute left-4 sm:left-6 lg:left-8 top-16 md:top-24 flex flex-col gap-1.5 text-brand-400"
        >
          <span className="block h-3 w-px bg-current" />
          <span className="block h-6 w-px bg-current" />
          <span className="block h-3 w-px bg-current" />
        </div>

        <div className="pl-6 sm:pl-8 lg:pl-10 max-w-4xl">
          {/* Eyebrow + status */}
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-sm border border-brand-400/30 bg-brand-500/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.26em] text-brand-200 backdrop-blur-sm">
              <span
                aria-hidden="true"
                className="block h-1.5 w-1.5 rounded-full bg-brand-400"
              />
              {t('eventDetail.eyebrow')}
            </span>
            <span
              className={cn(
                'rounded-sm border px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.2em] backdrop-blur-sm',
                upcoming
                  ? 'border-brand-400/40 bg-brand-500/15 text-brand-200'
                  : 'border-white/15 bg-white/5 text-white/70'
              )}
            >
              {upcoming
                ? t('eventDetail.statusUpcoming')
                : t('eventDetail.statusPast')}
            </span>
          </div>

          <h1
            id="event-hero-title"
            className="fx-text-balance font-bold leading-[1.08] text-white text-3xl sm:text-4xl md:text-5xl"
          >
            {title}
          </h1>

          {/* Meta row */}
          <dl className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4 text-sm">
            <div className="flex items-center gap-2.5">
              <Calendar
                className="h-4 w-4 text-brand-300"
                strokeWidth={2.25}
                aria-hidden="true"
              />
              <dt className="sr-only">{t('eventDetail.dateLabel')}</dt>
              <dd className="font-semibold uppercase tracking-[0.14em] text-white/85">
                <time dateTime={event.date}>{dateFormatted}</time>
              </dd>
            </div>
            <div className="flex items-center gap-2.5">
              <MapPin
                className="h-4 w-4 text-brand-300"
                strokeWidth={2.25}
                aria-hidden="true"
              />
              <dt className="sr-only">{t('eventDetail.locationLabel')}</dt>
              <dd className="font-semibold text-white/85">{location}</dd>
            </div>
          </dl>

          {/* External-link CTA (if url) */}
          {event.url && (
            <div className="mt-10">
              <a
                href={event.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-3 rounded-sm border-2 border-brand-300 bg-transparent px-6 py-3 text-sm font-bold uppercase tracking-[0.1em] text-white transition-[background-color,color,transform] duration-200 hover:bg-brand-500 hover:border-brand-500 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-900"
              >
                {t('eventDetail.officialSite')}
                <ExternalLink className="h-4 w-4" strokeWidth={2.5} />
              </a>
            </div>
          )}

          {/* Image alt — visually hidden for screen readers */}
          <span className="sr-only">{alt}</span>
        </div>

        {/* Bottom brand rule */}
        <div
          aria-hidden="true"
          className="absolute bottom-0 left-4 sm:left-6 lg:left-8 h-[3px] w-24 bg-brand-500"
        />
      </div>
    </section>
  )
}

/* ============================================================
   Event body — two-column layout: coordinate meta card on the left,
   technical prose on the right.
   ============================================================ */
function EventBody({
  event,
  paragraphs,
  city,
  country,
  venue,
  currentLang,
}: {
  event: FenixEvent
  paragraphs: string[]
  city: string
  country: string
  venue: string
  currentLang: SupportedLanguage
}) {
  const { t } = useTranslation()
  const dateFormatted = formatDateRange(event.date, event.dateEnd, currentLang)

  return (
    <Section tone="default" spacing="lg">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
        {/* Left column — event coordinates card */}
        <aside className="lg:col-span-4 lg:sticky lg:top-24">
          <div className="relative border border-ink-200 bg-white p-5 md:p-6">
            <div
              aria-hidden="true"
              className="absolute top-0 left-0 h-[3px] w-12 bg-brand-500"
            />
            <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-brand-600 mb-4">
              {t('eventDetail.overviewLabel')}
            </p>
            <dl className="space-y-5 text-sm">
              <div className="flex items-start gap-3">
                <Calendar
                  className="h-4 w-4 mt-0.5 text-brand-500 shrink-0"
                  strokeWidth={2.25}
                  aria-hidden="true"
                />
                <div className="min-w-0">
                  <dt className="text-[10px] font-bold uppercase tracking-[0.2em] text-ink-500">
                    {t('eventDetail.dateLabel')}
                  </dt>
                  <dd className="mt-0.5 font-semibold text-ink-800">
                    <time dateTime={event.date}>{dateFormatted}</time>
                  </dd>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Building2
                  className="h-4 w-4 mt-0.5 text-brand-500 shrink-0"
                  strokeWidth={2.25}
                  aria-hidden="true"
                />
                <div className="min-w-0">
                  <dt className="text-[10px] font-bold uppercase tracking-[0.2em] text-ink-500">
                    {t('eventDetail.venueLabel')}
                  </dt>
                  <dd className="mt-0.5 font-semibold text-ink-800">{venue}</dd>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin
                  className="h-4 w-4 mt-0.5 text-brand-500 shrink-0"
                  strokeWidth={2.25}
                  aria-hidden="true"
                />
                <div className="min-w-0">
                  <dt className="text-[10px] font-bold uppercase tracking-[0.2em] text-ink-500">
                    {t('eventDetail.cityLabel')}
                  </dt>
                  <dd className="mt-0.5 font-semibold text-ink-800">
                    {city}, {country}
                  </dd>
                </div>
              </div>
            </dl>

            {event.url && (
              <div className="mt-6 pt-6 border-t border-ink-200">
                <a
                  href={event.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex w-full items-center justify-center gap-2 rounded-sm border-2 border-brand-600 bg-white px-4 py-2.5 text-xs font-bold uppercase tracking-[0.14em] text-brand-700 transition-[background-color,color,transform] duration-200 hover:bg-brand-600 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
                >
                  {t('eventDetail.officialSite')}
                  <ExternalLink
                    className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    strokeWidth={2.75}
                  />
                </a>
              </div>
            )}
          </div>
        </aside>

        {/* Body column */}
        <div className="lg:col-span-8 max-w-3xl">
          <article
            className="space-y-6 text-base md:text-[17px] leading-[1.75] text-ink-700 fx-text-pretty"
            aria-label={t('eventDetail.title')}
          >
            {paragraphs.map((p, i) => (
              <p
                key={i}
                className={
                  i === 0
                    ? 'first-letter:float-left first-letter:mr-3 first-letter:font-display first-letter:font-bold first-letter:text-brand-600 first-letter:text-6xl first-letter:leading-[0.9] first-letter:pr-1'
                    : ''
                }
              >
                {p}
              </p>
            ))}
          </article>

          {/* Inline back-to-events link */}
          <div className="mt-12 pt-8 border-t border-ink-200">
            <LocalizedLink
              to="events"
              className="group inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-brand-600 transition-colors hover:text-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 rounded-sm"
            >
              <ArrowLeft
                className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-1"
                strokeWidth={2.75}
              />
              {t('eventDetail.back')}
            </LocalizedLink>
          </div>
        </div>
      </div>
    </Section>
  )
}

/* ============================================================
   Related events section
   ============================================================ */
function RelatedEvents({ current }: { current: FenixEvent }) {
  const { t } = useTranslation()
  const related = useMemo(() => getRelatedEvents(current, 2), [current])
  if (related.length === 0) return null

  return (
    <Section tone="muted" spacing="lg">
      <header className="mb-10 md:mb-12 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div className="max-w-2xl">
          <p className="mb-3 inline-flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.22em] text-brand-600">
            <span aria-hidden="true" className="block h-px w-8 bg-brand-500" />
            {t('eventDetail.related')}
          </p>
          <h2 className="fx-text-balance font-bold leading-tight text-ink-900 text-2xl md:text-3xl">
            {t('eventDetail.related')}
          </h2>
          <p className="mt-3 text-base leading-relaxed text-ink-600 fx-text-pretty">
            {t('eventDetail.relatedLead')}
          </p>
        </div>
        <LocalizedLink
          to="events"
          className="group inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-brand-600 transition-colors hover:text-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 rounded-sm"
        >
          {t('eventDetail.back')}
          <ArrowRight
            className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1"
            strokeWidth={2.75}
          />
        </LocalizedLink>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-7">
        {related.map((event) => (
          <EventCard
            key={event.slug}
            slug={event.slug}
            image={event.image}
            imageAlt={t(eventKey(event, 'alt'))}
            title={t(eventKey(event, 'title'))}
            location={t(eventKey(event, 'location'))}
            date={event.date}
          />
        ))}
      </div>
    </Section>
  )
}

/* ============================================================
   404 — "Event not found" fallback
   ============================================================ */
function EventNotFound({ slug }: { slug: string | undefined }) {
  const { t } = useTranslation()

  return (
    <main id="main-content" className="bg-ink-100">
      <section className="relative isolate overflow-hidden bg-ink-900 text-white pt-28 md:pt-36 pb-24 md:pb-28">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.08] fx-diagonal-accent"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -left-40 top-1/2 h-96 w-96 -translate-y-1/2 rounded-full bg-brand-500/20 blur-3xl"
        />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="mb-6 flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.3em] text-brand-300">
              <span aria-hidden="true" className="block h-px w-8 bg-brand-500" />
              <span>{t('errorPage.code404')}</span>
            </div>

            <p className="font-display font-bold tabular-nums text-brand-500/80 text-7xl md:text-8xl leading-none">
              {t('eventDetail.notFound.code')}
            </p>
            <h1 className="mt-5 fx-text-balance font-bold text-3xl sm:text-4xl md:text-5xl leading-[1.1] text-white">
              {t('eventDetail.notFound.title')}
            </h1>
            <p className="mt-5 max-w-xl fx-text-pretty text-base md:text-lg leading-relaxed text-white/80">
              {t('eventDetail.notFound.body')}
            </p>

            {slug && (
              <p className="mt-4 inline-flex items-center gap-2 rounded-sm border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-white/70">
                <span
                  className="block h-1.5 w-1.5 rounded-full bg-brand-400"
                  aria-hidden="true"
                />
                {t('errorPage.slugLabel')} <span className="font-mono normal-case text-white/90">{slug}</span>
              </p>
            )}

            <div className="mt-10 flex flex-wrap gap-4">
              <LocalizedLink
                to="events"
                className="group relative inline-flex items-center gap-3 rounded-sm bg-brand-500 px-6 py-3.5 text-sm font-bold uppercase tracking-[0.1em] text-white shadow-[0_10px_30px_-12px_rgba(74,155,220,0.55)] transition-[background-color,transform] duration-200 hover:bg-brand-400 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-900"
              >
                <span
                  aria-hidden="true"
                  className="absolute -left-[3px] top-1/2 block h-6 w-[3px] -translate-y-1/2 bg-white/90 transition-all duration-200 group-hover:h-8"
                />
                {t('eventDetail.notFound.backCta')}
                <ArrowRight className="h-4 w-4" strokeWidth={2.75} />
              </LocalizedLink>
              <LocalizedLink
                to="home"
                className="inline-flex items-center gap-2 rounded-sm border border-white/30 bg-white/5 px-6 py-3.5 text-sm font-bold uppercase tracking-[0.1em] text-white transition-colors duration-200 hover:border-brand-300 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-900"
              >
                <ArrowLeft className="h-4 w-4" strokeWidth={2.75} />
                {t('eventDetail.breadcrumbHome')}
              </LocalizedLink>
            </div>
          </div>
        </div>
      </section>
    </main>
  )
}

/* ============================================================
   Main component
   ============================================================ */
export default function EventDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const { t, i18n } = useTranslation()
  const currentLang = (i18n.language || 'it') as SupportedLanguage

  const event = useMemo(
    () => (slug ? getEventBySlug(slug) : undefined),
    [slug]
  )

  // Pre-compute translated strings BEFORE calling usePageSEO so the hook
  // receives the actual values (and keeps the same hook order on miss).
  const title = event ? t(eventKey(event, 'title')) : ''
  const excerpt = event ? t(eventKey(event, 'excerpt')) : ''
  const body = event ? t(eventKey(event, 'body')) : ''
  const alt = event ? t(eventKey(event, 'alt')) : ''
  const location = event ? t(eventKey(event, 'location')) : ''
  const city = event ? t(eventKey(event, 'city')) : ''
  const country = event ? t(eventKey(event, 'country')) : ''
  const venue = event ? t(eventKey(event, 'venue')) : ''

  const alternatePaths = useMemo(() => {
    if (!event) return undefined
    return {
      it: `/it/${localizedRoutes.events.it}/${event.slug}`,
      en: `/en/${localizedRoutes.events.en}/${event.slug}`,
    }
  }, [event])

  usePageSEO({
    title: event
      ? `${title} | ${t('meta.companyName')}`
      : `${t('eventDetail.notFound.title')} | ${t('meta.companyName')}`,
    description: event ? excerpt : t('eventDetail.notFound.body'),
    ogTitle: event
      ? `${title} — ${t('meta.companyName')}`
      : `${t('eventDetail.notFound.title')} — ${t('meta.companyName')}`,
    ogDescription: event ? excerpt : t('eventDetail.notFound.body'),
    ogImage: event?.image ?? '/images/events/mecspe-2023.jpg',
    ogType: event ? 'article' : 'website',
    alternatePaths,
  })

  const paragraphs = useMemo(() => {
    if (!body) return []
    return body
      .split(/\n\n+/)
      .map((p) => p.trim())
      .filter(Boolean)
  }, [body])

  if (!event) {
    return <EventNotFound slug={slug} />
  }

  const origin =
    typeof window !== 'undefined'
      ? window.location.origin
      : 'https://fenixgrind.it'
  const eventsPath = `/${currentLang}/${localizedRoutes.events[currentLang]}`
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: t('eventDetail.breadcrumbHome'),
        item: `${origin}/${currentLang}`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: t('eventDetail.breadcrumbEvents'),
        item: `${origin}${eventsPath}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: title,
        item: `${origin}${eventsPath}/${event.slug}`,
      },
    ],
  }

  return (
    <main id="main-content" className="bg-ink-100">
      <JsonLd id="breadcrumb" data={breadcrumbSchema} />
      <Breadcrumb eventTitle={title} />
      <EventHero
        event={event}
        title={title}
        location={location}
        alt={alt}
        currentLang={currentLang}
      />
      <EventBody
        event={event}
        paragraphs={paragraphs}
        city={city}
        country={country}
        venue={venue}
        currentLang={currentLang}
      />

      {/* Inline back CTA band — extra visibility above related */}
      <section
        aria-label={t('eventDetail.back')}
        className="bg-white border-t border-ink-200"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 md:py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-sm md:text-base text-ink-600 fx-text-pretty max-w-xl">
            {t('eventDetail.relatedLead')}
          </p>
          <LocalizedLink
            to="events"
            className="group inline-flex items-center gap-3 rounded-sm border-2 border-brand-600 bg-white px-5 py-2.5 text-sm font-bold uppercase tracking-[0.1em] text-brand-700 transition-[background-color,color,transform] duration-200 hover:bg-brand-600 hover:text-white hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={2.5} />
            {t('eventDetail.back')}
            <ArrowUpRight
              className="h-3.5 w-3.5 opacity-0 transition-all duration-200 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              strokeWidth={2.75}
            />
          </LocalizedLink>
        </div>
      </section>

      <RelatedEvents current={event} />
    </main>
  )
}
